import { Injectable } from '@angular/core';
import { QueryEntity, QueryConfig, Order } from '@datorama/akita';
import { MarketplaceStore, MarketplaceState } from './marketplace.store';
import { Observable, combineLatest } from 'rxjs';
import { MarketplaceItem, MarketplaceItemLimitType } from './models/marketplace-item.model';
import { ProcessProviderId } from './models/process-provider-id';
import { BalanceQuery } from '../../account/balance/balance.query';
import { map, shareReplay } from 'rxjs/operators';
import { config } from '../../shared/config';
import { MarketplaceItemStockType } from './models/marketplace-item-stock-type.model';

@Injectable({ providedIn: 'root' })
@QueryConfig({ sortBy: 'Id', sortByOrder: Order.DESC })
export class MarketplaceQuery extends QueryEntity<MarketplaceState> {
  constructor(
    protected store: MarketplaceStore,
    private balanceQuery: BalanceQuery
  ) {
    super(store);
  }

  /** All marketplace items. */
  selectMarketplaceItems(): Observable<MarketplaceItem[]> {
    return this.selectAll();
  }

  /** A specific marketplace item by ID. */
  selectMarketplaceItem(id: number): Observable<MarketplaceItem | undefined> {
    return this.selectEntity(id);
  }

  /** All marketplace items that match any of the specified types. */
  selectMarketplaceItemsOfType(...types: ProcessProviderId[]): Observable<MarketplaceItem[]> {
    return this.selectAll({ filterBy: (item) => types.includes(item.ProcessProviderId) });
  }

  /** Favourite marketplace items. */
  selectFavourites(): Observable<MarketplaceItem[]> {
    return this.selectAll({ filterBy: (item) => item.Favourite });
  }

  /** get the maximum number of purchases a given member can make */
  selectPurchaseLimit(id: number, memberId?: string, mappingId?: number): Observable<number> {
    return this._purchaseLimit(id, memberId, mappingId).pipe(map((x) => x?.limit || 0));
  }

  /** get the message to display when the maximum number of purchases is exceeded */
  selectPurchaseLimitMessage(id: number, memberId?: string, mappingId?: number): Observable<string> {
    return this._purchaseLimit(id, memberId, mappingId).pipe(map((x) => x?.errorMessage || ''));
  }

  /** get the price of a marketplaceItem  */
  getPrice(id: number): number | undefined {
    return this.getEntity(id)?.PointValue;
  }

  private _purchaseLimit(
    id: number,
    memberId?: string,
    mappingId?: number
  ): Observable<{ limit: number; errorMessage: string } | undefined> {
    return combineLatest([
      this.selectMarketplaceItem(id),
      this.balanceQuery.selectPurse(config.purseConfig.virtualLoyalty, memberId),
    ]).pipe(
      map(([item, balance]) => {
        if (item == undefined || balance == undefined) {
          return;
        }

        let limit: number;
        if (item.StockType === MarketplaceItemStockType.AttributeStockType && item.AttributeMappings) {
          let mapping = item.AttributeMappings.find((x) => x.AttributeMappingId === mappingId);
          limit = mapping ? mapping.AvailableStockCount : 0;
        } else {
          limit = item.AvailabilityCount;
        }

        let errorMessage = 'error.marketplace-notEnoughStock';

        const quantityLimitFromFunds = Math.floor(balance / item.PointValue);
        if (quantityLimitFromFunds < limit && item.PointValue) {
          limit = quantityLimitFromFunds;
          errorMessage = 'error.user-insufficientFunds';
        }

        if (item.MaxQuantity < limit) {
          limit = item.MaxQuantity;
          errorMessage = 'error.loyalty-memberLimitReached';
          if (item.LimitType !== MarketplaceItemLimitType.NoLimit) {
            switch (item.LimitType) {
              case MarketplaceItemLimitType.LimitByAccount:
                errorMessage = 'error.marketplace-accountLimitReached';
                break;
              case MarketplaceItemLimitType.LimitByMember:
                errorMessage = 'error.marketplace-memberLimitReached';
                break;
              default:
                errorMessage = 'error.loyalty-memberLimitReached';
                break;
            }
          }
        }
        return { limit, errorMessage };
      }),
      shareReplay(1)
    );
  }
}
